import { Box, Skeleton, Stack, styled, Tooltip, Typography } from '@mui/material';
import { DEFAULT_DATE_FORMAT_FNS, GroupMin } from '@schooly/api';
import { SECTION_ROW_COLUMNS } from '@schooly/constants/test-ids';
import {
  CheckIconV2,
  PlusIcon,
  SimpleButton,
  SimpleCard,
  TagSimple,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { format } from 'date-fns';
import { FC, PropsWithChildren, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { takeAttendancePath } from '../../../Groups/TakeAttendanceModal/take-attendance-utils';

import { GroupsWidgetData, GroupTrackingTag } from './useGroupsWidgetData';

const SKELETON_CARDS = 3;

interface GroupsWidgetProps {
  data: GroupsWidgetData;
  canViewAttendance: boolean;
  canManageAttendance: boolean;
}

export const GroupsWidget: FC<GroupsWidgetProps> = ({
  data,
  canViewAttendance,
  canManageAttendance,
}) => {
  const { tutorGroups, otherGroups, tagsByGroupId, isLoading } = data;

  const today = useMemo(() => format(new Date(), DEFAULT_DATE_FORMAT_FNS), []);

  const showTutorGroups = isLoading || tutorGroups.length > 0;
  const showOtherGroups = isLoading || otherGroups.length > 0;

  const renderCard = (group: GroupMin) => (
    <AttendanceGroupCard
      key={group.id}
      group={group}
      tags={canViewAttendance ? (tagsByGroupId[group.id] ?? []) : []}
      canManageAttendance={canManageAttendance}
      date={today}
    />
  );

  return (
    <Stack gap={2.5} mt={2}>
      {showTutorGroups && (
        <GroupList titleId="dashboard-teacher-TutorGroups">
          <GroupGrid>
            {isLoading
              ? Array.from({ length: SKELETON_CARDS }).map((_, i) => (
                  <Skeleton key={i} variant="rectangular" sx={{ minHeight: 56 }} />
                ))
              : tutorGroups.map(renderCard)}
          </GroupGrid>
        </GroupList>
      )}

      {showOtherGroups && (
        <GroupList titleId="dashboard-teacher-SubjectGroups">
          <GroupGrid>
            {isLoading
              ? Array.from({ length: SKELETON_CARDS }).map((_, i) => (
                  <Skeleton key={i} variant="rectangular" sx={{ minHeight: 56 }} />
                ))
              : otherGroups.map(renderCard)}
          </GroupGrid>
        </GroupList>
      )}
    </Stack>
  );
};

const AttendanceGroupCard: FC<{
  group: GroupMin;
  tags: GroupTrackingTag[];
  canManageAttendance: boolean;
  date: string;
}> = ({ group, tags, canManageAttendance, date }) => {
  const navigate = useNavigate();

  return (
    <GroupCard
      withBorder
      onItemClick={() => navigate(`/groups/${group.id}`)}
      actionAreaProps={{ sx: { px: 2 } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        minHeight={38}
        flex={1}
        minWidth={0}
      >
        <TypographyWithOverflowHint variant="h3" data-test-id={SECTION_ROW_COLUMNS.group_name}>
          {group.name}
        </TypographyWithOverflowHint>{' '}
        {tags.length > 0 && (
          <Stack direction="row" gap={0.5} flexShrink={0}>
            {tags.map((tag) => (
              <AttendanceTrackingTag
                key={tag.key}
                groupId={group.id}
                tag={tag}
                canManageAttendance={canManageAttendance}
                date={date}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </GroupCard>
  );
};

const AttendanceTrackingTag: FC<{
  groupId: string;
  tag: GroupTrackingTag;
  canManageAttendance: boolean;
  date: string;
}> = ({ groupId, tag, canManageAttendance, date }) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const showTakeAction = !tag.completed && canManageAttendance;

  return (
    <Tooltip
      arrow={false}
      onClick={(e) => {
        e.stopPropagation();
      }}
      title={
        showTakeAction ? (
          <SimpleButton
            startIcon={<PlusIcon />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                takeAttendancePath({
                  groupId,
                  date,
                  session: tag.session,
                  periodId: tag.periodId,
                }),
              );
            }}
          >
            <Typography variant="h3">
              {formatMessage({ id: 'dashboard-teacher-TakeAttendance' })}
            </Typography>
          </SimpleButton>
        ) : null
      }
      componentsProps={{
        tooltip: {
          sx: { p: 1, borderRadius: 0.5, pointerEvents: 'auto', margin: '2px !important' },
        },
      }}
    >
      <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
        <TagSimple
          color={tag.completed ? 'success' : 'attention'}
          direction="row"
          gap={0.5}
          justifyContent="center"
          data-test-id={SECTION_ROW_COLUMNS.attendance_tag}
          data-completed={tag.completed}
          sx={{
            '--icon-size': '14px',
            height: 24,
            width: !tag.label ? 24 : undefined,
            px: !tag.label ? 0.5 : undefined,
          }}
        >
          {tag.completed ? (
            <CheckIconV2 style={{ flexShrink: 0 }} />
          ) : (
            <Box
              sx={{
                width: 8,
                height: 8,
                flexShrink: 0,
                borderRadius: '50%',
                backgroundColor: 'currentColor',
              }}
            />
          )}
          {tag.label && (
            <Typography variant="h4" color="inherit" sx={{ fontWeight: 500 }}>
              {tag.label}
            </Typography>
          )}
        </TagSimple>
      </Box>
    </Tooltip>
  );
};

const GroupGrid: FC<PropsWithChildren> = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { sm: '1fr', md: 'repeat(3, 1fr)' },
      gap: 1,
    }}
  >
    {children}
  </Box>
);

const GroupList: FC<PropsWithChildren<{ titleId: string }>> = ({ titleId, children }) => (
  <Stack gap={1} data-scope>
    <Typography variant="h4">
      <FormattedMessage id={titleId} />
    </Typography>
    {children}
  </Stack>
);

export const GroupCard = styled(SimpleCard)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  '.card-actions': { minWidth: 0 },
  '&:hover': {
    '.MuiCardActionArea-focusHighlight': { background: 'transparent' },
    '.MuiCardActionArea-root': { background: theme.palette.common.lightBg },
  },
}));
